import { ControlledRadioGroup, type FormSetup } from '@if/mui'
import { Alert, Divider, Stack, Typography } from '@mui/material'
import { useUpdateBanner } from '@src/data/api/merchants-api/merchants-api'
import { BannerDto, BannerSchema } from '@src/data/types/BannerSchema'
import { EBannerOwnedFieldKey } from '@src/data/types/Constants'
import { Merchant } from '@src/data/types/Merchant'
import { useCallback, useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import CustomPromotionPanel from '../../../EditMerchant/components/CustomPromotionPanel'
import { TabFormMethods } from '../../../EditMerchant/components/tabs/MerchantGeneralTab'
import { useSyncFormDefaults } from '../../../EditMerchant/components/useSyncFormDefaults'
import { BannerSchemaType, BannerTabConfig } from './BannerGeneralTab'
import { getBannerDtoFromMerchant } from './bannerDto'

// String-typed so equality checks against the string[] field don't trip the enum-comparison rule.
const CUSTOM_BUYDOWN_KEY: string = EBannerOwnedFieldKey.CustomBuydown

// Owns the CustomBuydown key when the banner defines the buydown; releases it to children otherwise.
function reconcileOwnedFields(ownedFields: string[], bannerDefinesBuydown: boolean): string[] {
  const without = ownedFields.filter((f) => f !== CUSTOM_BUYDOWN_KEY)
  return bannerDefinesBuydown ? [...without, CUSTOM_BUYDOWN_KEY] : without
}

const BannerPromotionTabContent = ({ banner, defaults }: { banner: Merchant; defaults: BannerDto }) => {
  const { t } = useTranslation()
  const { control } = useFormContext<BannerDto>()
  useSyncFormDefaults<BannerDto>(defaults)

  const bannerDefinesBuydown = useWatch({ control, name: 'flashPlanDefinedInManageMerchant', defaultValue: false })

  return (
    <Stack gap={2}>
      <Typography variant="h6">{t('banner.customBuydown.title')}</Typography>

      <ControlledRadioGroup
        control={control}
        name="flashPlanDefinedInManageMerchant"
        label={t('banner.customBuydown.modeLabel')}
        variant="card"
        options={[
          {
            value: false,
            title: t('banner.customBuydown.merchantTitle'),
            description: t('banner.customBuydown.merchantDescription'),
          },
          {
            value: true,
            title: t('banner.customBuydown.ownedTitle'),
            description: t('banner.customBuydown.ownedDescription'),
          },
        ]}
      />

      {bannerDefinesBuydown ? (
        <>
          <Divider />
          <CustomPromotionPanel id={banner.id} />
        </>
      ) : (
        <Alert severity="info">{t('banner.customBuydown.merchantManagedNotice')}</Alert>
      )}
    </Stack>
  )
}

/**
 * Promotion tab config. `flashPlanDefinedInManageMerchant` drives everything: when true the banner
 * owns CustomBuydown and pushes one plan to every child, else ownership is released to the children.
 * `ownedFields` is reconciled from that flag on save so the key always matches it.
 */
export function useBannerPromotionTab(banner: Merchant): BannerTabConfig<BannerSchemaType> {
  const [updateBanner] = useUpdateBanner()

  const defaults = useMemo(() => getBannerDtoFromMerchant(banner), [banner])

  const useFormSetup = useCallback(
    (): FormSetup<BannerSchemaType> => ({
      schema: BannerSchema,
      defaultValues: defaults,
    }),
    [defaults],
  )

  const onSubmit = useCallback(
    async (values: BannerDto, methods: TabFormMethods<BannerSchemaType>) => {
      const payload: BannerDto = {
        ...values,
        ownedFields: reconcileOwnedFields(values.ownedFields, values.flashPlanDefinedInManageMerchant),
      }
      await updateBanner(payload)
      methods.reset(payload)
    },
    [updateBanner],
  )

  const children = useCallback(
    () => <BannerPromotionTabContent banner={banner} defaults={defaults} />,
    [banner, defaults],
  )

  return { useFormSetup, onSubmit, children }
}
